import { Auction, AuctionLot } from '../types/auction.types';
import { serverNow } from './serverClock';

/**
 * SALE STATE — derived, display-only.
 *
 * 🔴 Why this exists: the homepage used to hide a sale once `endDate` passed. On Sunday that
 * blanked the page at 8 PM **while lots were still open in soft-close**. Visibility must be
 * driven by whether LOTS ARE STILL OPEN, never by the scheduled end date.
 *
 * The engine never writes auction status. 'closing' and 'ended' are DERIVED here from lot
 * timing. Only 'confirmed' (and 'draft'/'scheduled') are authoritative stored values — the
 * auctioneer owns those.
 */

export type SaleState = 'draft' | 'scheduled' | 'live' | 'closing' | 'ended' | 'confirmed';

export const DEFAULT_SOFT_CLOSE_WINDOW = 180; // seconds — mirrors the engine default
export const DEFAULT_COOL_OFF_WINDOW = 3600; // seconds — UI default for the tally window

const toDate = (v: any): Date | null => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v?.toDate === 'function') return v.toDate();
  // Plain { seconds, nanoseconds } — a Timestamp that has been through JSON, or any
  // path that lost the prototype. utils/lotTiming.ts has always handled this shape;
  // this module did not, so such a value fell through to `new Date(object)` =>
  // Invalid Date => null => the lot read as having NO end time. Harmless-looking
  // (the UI degrades to "—" rather than a fake 0:00) but it silently drops the
  // countdown. Caught by a lotState test; brought in line with lotTiming.
  if (typeof v?.seconds === 'number') {
    const ms = v.seconds * 1000 + (typeof v.nanoseconds === 'number' ? v.nanoseconds / 1e6 : 0);
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

/** Legacy 'completed' (closedown scripts) means the same thing as 'ended'. */
export const normalizeStatus = (raw: any): SaleState => (raw === 'completed' ? 'ended' : (raw as SaleState));

/** A lot's effective end — reflects any soft-close extension. */
export const lotEndTime = (lot: AuctionLot): Date | null =>
  toDate((lot as any).actualEndTime) || toDate((lot as any).scheduledEndTime);

/**
 * The HONEST end of bidding on a lot: the earlier of its effective end (lotEndTime) and the sale's
 * biddingHardStop. The engine caps every lot at scheduled + maxExtensionSeconds, which can never be
 * later than a hard stop of (last scheduled + maxExtensionSeconds) — so today this changes nothing.
 * It covers a hard stop set by hand earlier than that, where the rules refuse a bid the lot's own
 * clock would still allow. DISPLAY ONLY: no browser code refuses a bid on any computed time.
 */
export const effectiveLotEnd = (lot: AuctionLot, auction?: { biddingHardStop?: unknown } | null): Date | null => {
  const end = lotEndTime(lot);
  const stop = toDate((auction as any)?.biddingHardStop);
  if (!end) return null;
  return stop && stop.getTime() < end.getTime() ? stop : end;
};

/** Open = its effective end is still in the future. Extensions keep a lot open past endDate. */
export const isLotOpen = (lot: AuctionLot, now = new Date(serverNow())): boolean => {
  const end = lotEndTime(lot);
  return !!end && end.getTime() > now.getTime();
};

/** In soft-close = open AND (already extended OR inside the soft-close window). */
export const isLotInSoftClose = (lot: AuctionLot, auction: Auction, now = new Date(serverNow())): boolean => {
  if (!isLotOpen(lot, now)) return false;
  if (((lot as any).extensionCount ?? 0) > 0) return true;
  const end = lotEndTime(lot);
  if (!end) return false;
  const windowMs = (auction.softCloseWindow ?? DEFAULT_SOFT_CLOSE_WINDOW) * 1000;
  const remaining = end.getTime() - now.getTime();
  return remaining > 0 && remaining <= windowMs;
};

export const openLots = (auction: Auction, now = new Date(serverNow())): AuctionLot[] =>
  (auction.lots || []).filter((l) => isLotOpen(l, now));

/* ═══════════════════════════════════════════════════════════════════════════
   LOT STATE — the ONE vocabulary every tile speaks.
   ═══════════════════════════════════════════════════════════════════════════

   Before this existed, the homepage asked saleState.isLotInSoftClose() and the
   sale page asked lotTiming.isInSoftCloseWindow(). Those are different questions
   with different answers (the first is true whenever extensionCount > 0, even
   outside the window), so the same lot could render "closing" on one page and
   normal on the other. lotState() replaces both for display purposes.

   🔴 THE SETTLING STATE — why a countdown here doesn't lie.

   Soft close moves the finish line. The server extends a lot INSIDE the bid
   transaction (functions/src/index.ts: patched.actualEndTime = extendEndTime(...)),
   writing the new time into auctions/{id}.lots[]. A subscribed client sees it —
   but only after the snapshot arrives. In the gap between a bid landing at T-0.5s
   and that snapshot, a naive clock would already be showing CLOSED for a lot that
   is very much still open.

   So a lot whose clock has just run out does NOT go straight to closed. It enters
   'settling' for SETTLING_GRACE_MS and says "Closing…" — committing to nothing.
   If an extension arrives, endsAt jumps forward, the elapsed-past-end goes
   negative, and the lot returns to 'closing_now' on its own. No extra state, no
   reconciliation: the same arithmetic that produces the countdown produces the
   recovery. The grace also absorbs small client clock skew, which we otherwise
   have no cheap way to correct (Firestore exposes no server clock to the client).

   🔒 PRIVACY — closed_sold carries the bidder NUMBER and the bidder UID, and
   NEVER a name. AuctionLot.currentBidderName is @deprecated ("the public doc must
   not carry bidder NAMES") and was stripped from live data by
   strip-bidder-names.mjs. This function builds a fresh object field by field and
   never spreads the lot, so a legacy name still sitting on an old lot document
   cannot reach a rendered surface through here. There is a test asserting exactly
   that. Do not add a name field to LotState.
*/

/** How long a lot says "Closing…" after its clock runs out, before it commits to CLOSED. */
export const SETTLING_GRACE_MS = 5000;

/** Inside this many seconds, a lot is "closing now" (the loud, red state). */
export const CLOSING_NOW_SECONDS = 60;

export type LotStateKind =
  | 'upcoming'
  | 'closing_soon'
  | 'closing_now'
  | 'settling'
  | 'closed_sold'
  | 'closed_unsold'
  | 'unknown';

export interface LotState {
  kind: LotStateKind;
  /** Effective end time (reflects soft-close extensions). null only when 'unknown'. */
  endsAt: Date | null;
  /** Whole seconds until endsAt; 0 once past. */
  secondsRemaining: number;
  /** Soft close has already moved this lot's finish line at least once. */
  extended: boolean;
  extensionCount: number;
  /** closed_sold only — hammer in CENTS. */
  hammerCents?: number;
  /** closed_sold only — PUBLIC bidder number (e.g. "3607"). Never a name. */
  winnerBidderNumber?: string;
  /** closed_sold only — winner's uid, for an "own bid" comparison by the caller. */
  winnerUid?: string;
  bidCount: number;
}

/**
 * Did this lot sell? Uses bidCount, which the server writes as the authoritative
 * count of bid documents (functions/src/index.ts: patched.bidCount = lotBidsSnap.size).
 *
 * Deliberately NOT `currentBid > startingBid`: the importer seeds currentBid =
 * startingBid on every lot, so that comparison misreports (same trap documented in
 * utils/lotDisplay.ts). Also deliberately NOT lot.status — its 'sold'/'unsold'
 * values are only ever written by the import-era import-results.js, so on a
 * natively-run sale it still holds whatever the CSV importer set.
 */
const lotSold = (lot: AuctionLot): boolean => {
  const n = Number((lot as any).bidCount);
  return Number.isFinite(n) && n > 0;
};

/**
 * The single source of display truth for one lot at one instant.
 * PURE — pass nowMs in; never reads the clock itself, so it is fully testable.
 */
export const lotState = (lot: AuctionLot, auction: Auction, nowMs: number): LotState => {
  const endsAt = effectiveLotEnd(lot, auction);
  const extensionCount = Number((lot as any).extensionCount) || 0;
  const extended = extensionCount > 0;
  const bidCount = Number((lot as any).bidCount) || 0;

  if (!endsAt) {
    // No usable end time at all — say nothing rather than guess. An armed sale
    // always has one; an imported-but-never-armed lot does not.
    return { kind: 'unknown', endsAt: null, secondsRemaining: 0, extended, extensionCount, bidCount };
  }

  const msRemaining = endsAt.getTime() - nowMs;
  const secondsRemaining = Math.max(0, Math.floor(msRemaining / 1000));
  const base = { endsAt, secondsRemaining, extended, extensionCount, bidCount };

  if (msRemaining > 0) {
    const windowSec = auction.softCloseWindow ?? DEFAULT_SOFT_CLOSE_WINDOW;
    if (secondsRemaining <= CLOSING_NOW_SECONDS) return { ...base, kind: 'closing_now' };
    if (secondsRemaining <= windowSec) return { ...base, kind: 'closing_soon' };
    return { ...base, kind: 'upcoming' };
  }

  // Past the end. Hold for the grace period before committing — an extension
  // written by a last-second bid may still be in flight to this client.
  if (-msRemaining < SETTLING_GRACE_MS) return { ...base, kind: 'settling' };

  if (!lotSold(lot)) return { ...base, kind: 'closed_unsold' };

  return {
    ...base,
    kind: 'closed_sold',
    hammerCents: Number((lot as any).currentBid) || 0,
    // PUBLIC identity only — see the privacy note above.
    winnerBidderNumber: (lot as any).currentBidderNumber || undefined,
    winnerUid: (lot as any).currentBidder || undefined,
  };
};

/** Convenience predicates so callers don't string-compare kinds everywhere. */
export const isLotClosedState = (s: LotState): boolean =>
  s.kind === 'closed_sold' || s.kind === 'closed_unsold';

/** Still accepting bids as far as the UI is concerned. 'settling' counts as OPEN —
 *  we do not know yet, and disabling a bid button on a lot that then extends is
 *  worse than leaving it live for five seconds. */
export const isLotBiddableState = (s: LotState): boolean =>
  s.kind === 'upcoming' || s.kind === 'closing_soon' || s.kind === 'closing_now' || s.kind === 'settling';

/** Soonest effective end among still-open lots — drives the clock's cadence. */
export const soonestLotEndMs = (lots: AuctionLot[], nowMs: number): number | null => {
  let soonest: number | null = null;
  for (const l of lots || []) {
    const e = lotEndTime(l);
    if (!e) continue;
    const t = e.getTime();
    if (t <= nowMs) continue;
    if (soonest === null || t < soonest) soonest = t;
  }
  return soonest;
};

/**
 * Derive what to SHOW. Authoritative stored states win where they should:
 *  - 'confirmed' → auctioneer locked it; always wins.
 *  - 'draft'     → never public.
 *  - otherwise   → LOT-DRIVEN: any lot open ⇒ live/closing; none open ⇒ ended.
 * The scheduled endDate is deliberately NOT used to hide anything.
 */
export const deriveSaleState = (auction: Auction, now = new Date(serverNow())): SaleState => {
  const stored = normalizeStatus(auction.status);
  if (stored === 'confirmed') return 'confirmed';
  if (stored === 'draft') return 'draft';

  const open = openLots(auction, now);
  if (open.length > 0) {
    const closing = open.some((l) => isLotInSoftClose(l, auction, now));
    return closing ? 'closing' : 'live';
  }

  // No open lots. If it never started, it's still scheduled; otherwise it's ended.
  const start = toDate(auction.startDate);
  if (stored === 'scheduled' && start && start.getTime() > now.getTime()) return 'scheduled';
  return 'ended';
};

/** Bidding is allowed only while lots are actually open. */
export const isBiddable = (state: SaleState): boolean => state === 'live' || state === 'closing';

/**
 * THE definition of an OPEN sale: publicly visible (not a draft) and live or closing now.
 * PublicAuctionList's active sales and the home page's live-sale block both use this, so the
 * two cannot disagree about whether a sale is open.
 */
export const isSaleOpen = (auction: Auction, now = new Date(serverNow())): boolean =>
  isPubliclyVisible(auction) && isBiddable(deriveSaleState(auction, now));

/** Public visibility: everything except drafts. Notably NOT gated on endDate. */
export const isPubliclyVisible = (auction: Auction): boolean => normalizeStatus(auction.status) !== 'draft';

export const SALE_STATE_LABEL: Record<SaleState, string> = {
  draft: 'Draft',
  scheduled: 'Upcoming',
  live: 'Live Now',
  closing: 'Closing',
  ended: 'Ended — Tallying',
  confirmed: 'Final Results',
};

/* ═══════════════════════════════════════════════════════════════════════════
   DISPUTE DISPLAY — the ONE place the words for a disputed sale live.
   ═══════════════════════════════════════════════════════════════════════════
   auctions/{id}.disputeOpen (Oct 2026) is DISPLAY ONLY: nothing in bidding, closing, closedown or
   invoicing reads it. While it is true, every public surface that would spell out a closed / ended
   / sold result says "under review" instead. Each surface asks these helpers rather than holding
   its own string — a string repeated per file is how one label was missed the first time.
   Lots with no bids keep "Unsold" / "No bids": that never claimed a sale.

   THREE STATES for a closed lot (closedLotStatus):
     'to-be-relisted'  its lot number is on auction.disputedLots (the published relist list)
     'under-review'    the sale is disputed (disputeOpen) and the lot is not (yet) on that list
     'closed'          neither — the ordinary wording
   A lot on the relist list reads "to be relisted" even if disputeOpen is later switched off,
   so the old sale can never go back to calling a relisted lot "Sold". */

/** True while the sale's results are under review (auction.disputeOpen). */
export const isUnderReview = (a: { disputeOpen?: boolean } | null | undefined): boolean => !!a?.disputeOpen;

type LabelCase = 'upper' | 'lower' | 'sentence';
const inCase = (s: string, c: LabelCase): string =>
  c === 'upper' ? s.toUpperCase() : c === 'lower' ? s.toLowerCase() : s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

export type ClosedLotStatus = 'closed' | 'under-review' | 'to-be-relisted';
const CLOSED_LOT_WORD: Record<ClosedLotStatus, string> = {
  'closed': 'closed',
  'under-review': 'under review',
  'to-be-relisted': 'to be relisted',
};

type DisputeFields = { disputeOpen?: boolean; disputedLots?: unknown } | null | undefined;
const lotKey = (v: unknown): string => String(v ?? '').trim();

/** Is this lot's number on the sale's published relist list (auction.disputedLots)? */
export const isOnRelistList = (a: DisputeFields, lot: { lotNumber?: unknown } | null | undefined): boolean =>
  Array.isArray(a?.disputedLots) && !!lot && (a!.disputedLots as unknown[]).some((n) => lotKey(n) === lotKey(lot.lotNumber));

/** Which of the three closed-lot states applies (see the header above). */
export const closedLotStatus = (a: DisputeFields, lot: { lotNumber?: unknown } | null | undefined): ClosedLotStatus =>
  isOnRelistList(a, lot) ? 'to-be-relisted' : isUnderReview(a as any) ? 'under-review' : 'closed';

/** True for either dispute state (anything but the ordinary 'closed'). */
export const isDisputeStatus = (s: ClosedLotStatus): boolean => s !== 'closed';

/** A closed lot's state word — "closed" / "under review" / "to be relisted" — in the element's case. */
export const closedLotWord = (s: ClosedLotStatus, c: LabelCase): string => inCase(CLOSED_LOT_WORD[s], c);

/** What follows a disputed lot's amount: "— under review" / "— to be relisted" ('' when not disputed). */
export const closedPriceSuffix = (s: ClosedLotStatus): string => (s === 'closed' ? '' : `— ${CLOSED_LOT_WORD[s]}`);

/** How many lots are on the published relist list. */
export const relistCount = (a: DisputeFields): number => (Array.isArray(a?.disputedLots) ? (a!.disputedLots as unknown[]).length : 0);

/**
 * The relist-list line under the dispute notice. ⚠ The cadence, cutoff and address belong to the
 * October 2026 DeRosa dispute (relist list published daily at 8:00 PM ET until 5:00 PM ET Fri 23 Oct).
 * Another dispute needs this wording changed — here, and only here.
 */
export const relistListNotice = (count: number): string =>
  `${count} lot${count === 1 ? ' is' : 's are'} currently on the relist list. Updated daily at 8:00 PM ET. ` +
  'Lots can be added until 5:00 PM ET on Friday 23 October — email the lot numbers to dan@schmidtauctions.com.';

/**
 * "Updated 21 October, 8:15 PM ET — no additions" from auction.disputedLotsUpdatedAt and the optional
 * auction.disputedLotsUpdateNote ('' when there is no timestamp; the " — note" part only when the note
 * is a non-empty string). Both are written by set-disputed-lots.mjs — a daily review with no change is
 * a --touch run, so the time moves WITH a note saying why the list did not. The timestamp goes through
 * toDate() (Timestamp-safe — never new Date(Timestamp)), and is always shown in Eastern time.
 */
export const relistUpdatedLabel = (ts: unknown, note?: unknown): string => {
  const d = toDate(ts);
  if (!d) return '';
  const tz = 'America/New_York';
  const day = d.toLocaleDateString('en-GB', { timeZone: tz, day: 'numeric', month: 'long' });
  const time = d.toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' });
  const n = typeof note === 'string' ? note.trim() : '';
  return `Updated ${day}, ${time} ET${n ? ` — ${n}` : ''}`;
};

/** SALE_STATE_LABEL, except an ended / confirmed sale reads "Under Review" while disputed. */
export const saleStateLabelFor = (state: SaleState, a: { disputeOpen?: boolean } | null | undefined): string =>
  isUnderReview(a) && (state === 'ended' || state === 'confirmed') ? 'Under Review' : SALE_STATE_LABEL[state];

/** The rest of the dispute wording, each replacing the label named beside it. */
export const DISPUTE_WORDS = {
  highBid: 'High bid',                     // replaces "Sold" / "Sold (provisional)" / "Final price"
  youAreHighBidder: 'You are the high bidder', // replaces "You won"
  hideLots: 'Hide Lots Under Review',      // replaces "Hide Closed Lots"
  biddingEnded: 'Bidding ended',           // replaces "Closed <date>"
  showRelistOnly: 'Show only lots to be relisted', // sale-page filter; linkable via RELIST_VIEW
  noRelistLots: 'No lots are on the relist list yet.', // empty state of that filter
} as const;

/** The linkable relist view: /sale/{id}?show=relist  (e.g. for an email). */
export const RELIST_VIEW = { param: 'show', value: 'relist' } as const;

/**
 * The sale-page lot filters, with the three closed-lot states kept SEPARATE — never "closed or not":
 *   relistOnly  -> exactly the lots on the relist list (closedLotStatus 'to-be-relisted').
 *   hideClosed  -> hides closed lots EXCEPT those on the relist list. On a disputed sale that hides
 *                  exactly the 'under-review' lots, so switching it on leaves the set going back up.
 *                  Lots still open (or settling) are never hidden.
 */
export const passesLotFilters = (
  a: DisputeFields,
  lot: { lotNumber?: unknown },
  state: LotState,
  opts: { hideClosed: boolean; relistOnly: boolean },
): boolean => {
  const status = closedLotStatus(a, lot);
  if (opts.relistOnly) return status === 'to-be-relisted';
  if (!opts.hideClosed) return true;
  if (!isLotClosedState(state)) return true;
  return status === 'to-be-relisted';
};

/** "N of M lots sold", or its under-review form (with the relist count once a list is published). */
export const lotsSoldSummary = (sold: number, total: number, underReview: boolean, relisted = 0): string =>
  underReview
    ? `${sold} of ${total} lots with a high bid — under review${relisted ? ` · ${relisted} ${CLOSED_LOT_WORD['to-be-relisted']}` : ''}`
    : `${sold} of ${total} lots sold`;

/* ─────────────────────────────────────────────────────────────────────────────────────────────
   REGISTRATION GATE (Oct 2026). Whether a sale offers a Register path at all.
     'bid-off'  auction.bidOffFor is set: a dispute-resolution sale, open ONLY to the registered
                bidders of the original sale (copied in by open-bid-off.mjs). No registration path.
     'closed'   auction.registrationClosed === true (set-registration-closed.mjs). No registration path.
     'open'     everything else — unchanged behaviour.
   The UI hides the button; the registerForSale Cloud Function refuses the same two cases, which is
   the actual gate (a hidden button is not one).
   ───────────────────────────────────────────────────────────────────────────────────────────── */
export type RegistrationGate = 'open' | 'closed' | 'bid-off';
export const registrationGate = (a: { bidOffFor?: unknown; registrationClosed?: unknown } | null | undefined): RegistrationGate =>
  a?.bidOffFor ? 'bid-off' : a?.registrationClosed === true ? 'closed' : 'open';
export const REGISTRATION_GATE_TEXT: Record<Exclude<RegistrationGate, 'open'>, string> = {
  closed: 'Registration for this sale is closed.',
  'bid-off': 'Participation in this sale is limited to bidders registered for the original sale.',
};
/** The line shown in place of a Register button ('' when registration is open). */
export const registrationClosedText = (a: Parameters<typeof registrationGate>[0]): string => {
  const g = registrationGate(a);
  return g === 'open' ? '' : REGISTRATION_GATE_TEXT[g];
};
